import { escapeHtml } from "./pdfUtils";

const DEFAULT_THEME = {
  primary: "#7d6bff",
  secondary: "#5ef0d8",
  tertiary: "#f7c35f",
};

function sanitizeColor(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
  if (/^(rgb|rgba|hsl|hsla)\(/i.test(raw)) return raw;
  return fallback;
}

function renderCalendarCells({ calendarDays, luckyDaysByDay, topDaySet }) {
  return calendarDays.map((day, idx) => {
    if (!day) return `<div class="day-cell is-empty" data-key="empty-${idx}"></div>`;
    const score = Number(luckyDaysByDay?.[day]);
    const hasScore = Number.isFinite(score);
    const isTop = topDaySet.has(day);
    const scoreChip = isTop
      ? `<span class="day-chip is-top">★</span>`
      : hasScore
        ? `<span class="day-chip">${escapeHtml(`${Math.round(score)}%`)}</span>`
        : "";
    return `
      <div class="day-cell ${isTop ? "is-top" : ""} ${hasScore ? "has-score" : ""}" data-key="day-${idx}">
        <div class="day-num">${escapeHtml(day)}</div>
        ${scoreChip}
      </div>
    `;
  }).join("");
}

export function buildWeddingCompatibilityPdfHtml({
  exportedAt,
  brideDob,
  groomDob,
  monthLabel,
  year,
  weekdayLabels = [],
  calendarDays = [],
  luckyDaysByDay = {},
  topDays = [],
  labels = {},
  emptyState = null,
  theme = {},
}) {
  const l = labels;
  const themePrimary = sanitizeColor(theme.primary, DEFAULT_THEME.primary);
  const themeSecondary = sanitizeColor(theme.secondary, DEFAULT_THEME.secondary);
  const themeTertiary = sanitizeColor(theme.tertiary, DEFAULT_THEME.tertiary);
  const topDaySet = new Set(topDays.map((item) => Number(item.day)).filter(Number.isFinite));
  const weekdayGrid = weekdayLabels.map((weekday) => `<div class="weekday">${escapeHtml(weekday)}</div>`).join("");
  const calendarGrid = renderCalendarCells({ calendarDays, luckyDaysByDay, topDaySet });
  const topDaysHtml = topDays.length
    ? topDays.map((item) => `
      <div class="top-day-card">
        <div class="top-day-meta">
          <div class="top-day-date">${escapeHtml(`${monthLabel} ${item.day}, ${year}`)}</div>
          <div class="top-day-note">${escapeHtml(item.note || "")}</div>
          ${Array.isArray(item.reasons) && item.reasons.length ? `<div class="top-day-reasons">${item.reasons.map((reason) => `<div>• ${escapeHtml(reason)}</div>`).join("")}</div>` : ""}
        </div>
        <div class="top-day-score">${escapeHtml(`${Math.round(Number(item.score) || 0)}%`)}</div>
      </div>
    `).join("")
    : `
      <div class="empty-card">
        <div class="empty-title">${escapeHtml(emptyState?.title || "")}</div>
        <div class="empty-desc">${escapeHtml(emptyState?.desc || "")}</div>
        <div class="empty-hint">${escapeHtml(emptyState?.hint || "")}</div>
      </div>
    `;

  return `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400&display=swap" rel="stylesheet">
    <style>
      :root {
        --accent-primary: ${themePrimary};
        --accent-secondary: ${themeSecondary};
        --accent-tertiary: ${themeTertiary};
      }
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: "DM Sans", "Inter", "Segoe UI", Arial, sans-serif;
        color: #e8ebff;
        background: #050816;
      }
      .sheet {
        border-radius: 18px;
        border: 1px solid var(--accent-secondary);
        background: #090d23;
        overflow: hidden;
      }
      .header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-end;
        padding: 24px 28px 18px;
        border-bottom: 1px solid color-mix(in srgb, var(--accent-primary) 28%, transparent);
      }
      .brand {
        font-family: "Playfair Display", serif;
        font-size: 22px;
        color: var(--accent-secondary);
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .eyebrow {
        font-size: 10px;
        color: #99a3c4;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        margin-top: 4px;
      }
      .title {
        margin-top: 10px;
        font-family: "Playfair Display", serif;
        font-size: 28px;
        color: #f6f7ff;
      }
      .date-meta {
        text-align: right;
        font-family: "DM Mono", monospace;
        font-size: 10px;
        line-height: 1.7;
        color: #95a0bf;
      }
      .date-meta strong {
        display: block;
        font-size: 12px;
        color: var(--accent-primary);
      }
      .content {
        padding: 22px 24px 20px;
      }
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        margin-bottom: 14px;
      }
      .info-card {
        border: 1px solid rgba(255,255,255,0.14);
        border-radius: 12px;
        background: rgba(255,255,255,0.03);
        padding: 10px 12px;
      }
      .label {
        font-size: 10px;
        letter-spacing: 0.15em;
        color: #95a0bf;
        text-transform: uppercase;
        margin-bottom: 6px;
      }
      .value {
        font-size: 13px;
        color: #f1f3ff;
      }
      .result-grid {
        display: grid;
        grid-template-columns: 1fr 0.9fr;
        gap: 14px;
      }
      .panel {
        border-radius: 14px;
        border: 1px solid rgba(255,255,255,0.12);
        background: rgba(255,255,255,0.03);
        padding: 14px;
      }
      .panel-title {
        font-family: "Playfair Display", serif;
        color: #f6f7ff;
        font-size: 19px;
        margin-bottom: 10px;
      }
      .weekday-row, .calendar-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 6px;
      }
      .weekday {
        text-align: center;
        font-size: 10px;
        color: #9aa5c8;
      }
      .calendar-grid { margin-top: 7px; }
      .day-cell {
        aspect-ratio: 1;
        border-radius: 9px;
        border: 1px solid transparent;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
      }
      .day-cell.has-score {
        border-color: color-mix(in srgb, var(--accent-primary) 34%, transparent);
        background: color-mix(in srgb, var(--accent-primary) 10%, transparent);
      }
      .day-cell.is-top {
        border-color: color-mix(in srgb, var(--accent-secondary) 64%, transparent);
        background: linear-gradient(135deg, color-mix(in srgb, var(--accent-secondary) 18%, transparent), color-mix(in srgb, var(--accent-primary) 12%, transparent));
      }
      .day-cell.is-empty {
        border-color: transparent;
        background: transparent;
      }
      .day-num {
        font-size: 11px;
        color: #edf0ff;
      }
      .day-chip {
        font-size: 8px;
        color: var(--accent-primary);
      }
      .day-chip.is-top {
        color: var(--accent-tertiary);
      }
      .top-days {
        display: flex;
        flex-direction: column;
        gap: 9px;
      }
      .top-day-card {
        border: 1px solid color-mix(in srgb, var(--accent-secondary) 36%, transparent);
        background: color-mix(in srgb, var(--accent-secondary) 8%, transparent);
        border-radius: 10px;
        padding: 10px 11px;
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: center;
      }
      .top-day-date {
        color: #f6f8ff;
        font-size: 13px;
        font-family: "Playfair Display", serif;
      }
      .top-day-note {
        color: #9ca6c8;
        font-size: 10px;
        line-height: 1.45;
        margin-top: 3px;
      }
      .top-day-score {
        color: var(--accent-secondary);
        font-family: "Playfair Display", serif;
        font-size: 22px;
        white-space: nowrap;
      }
      .empty-card {
        border: 1px solid rgba(255,255,255,0.14);
        border-radius: 10px;
        padding: 12px;
        background: rgba(255,255,255,0.03);
      }
      .empty-title {
        font-size: 16px;
        font-family: "Playfair Display", serif;
        color: #f4f6ff;
        margin-bottom: 5px;
      }
      .empty-desc {
        font-size: 11px;
        color: #b5bfdc;
      }
      .empty-hint {
        margin-top: 7px;
        color: #8f9abb;
        font-size: 10px;
      }
      .footer {
        margin-top: 12px;
        padding: 0 24px 14px;
        font-size: 10px;
        color: #8f9abb;
        font-family: "DM Mono", monospace;
        letter-spacing: 0.12em;
        display: flex;
        justify-content: space-between;
      }
      @media print {
        html, body { height: 100%; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
    <section class="sheet">
      <header class="header">
        <div>
          <div class="brand">Ecliptica</div>
          <div class="eyebrow">${escapeHtml(l.eyebrow)}</div>
          <div class="title">${escapeHtml(l.title)}</div>
        </div>
        <div class="date-meta">
          <strong>${escapeHtml(l.report)}</strong>
          <div>${escapeHtml(l.exported)}: ${escapeHtml(exportedAt)}</div>
        </div>
      </header>
      <main class="content">
        <div class="info-grid">
          <div class="info-card">
            <div class="label">${escapeHtml(l.brideDob)}</div>
            <div class="value">${escapeHtml(brideDob)}</div>
          </div>
          <div class="info-card">
            <div class="label">${escapeHtml(l.groomDob)}</div>
            <div class="value">${escapeHtml(groomDob)}</div>
          </div>
          <div class="info-card" style="grid-column: 1 / -1;">
            <div class="label">${escapeHtml(l.focusMonth)}</div>
            <div class="value">${escapeHtml(`${monthLabel} ${year}`)}</div>
          </div>
        </div>
        <div class="result-grid">
          <section class="panel">
            <div class="panel-title">${escapeHtml(l.calendarSection)}</div>
            <div class="weekday-row">${weekdayGrid}</div>
            <div class="calendar-grid">${calendarGrid}</div>
          </section>
          <section class="panel">
            <div class="panel-title">${escapeHtml(l.topDaysSection)}</div>
            <div class="top-days">
              ${topDaysHtml}
            </div>
          </section>
        </div>
      </main>
      <footer class="footer">
        <span>${escapeHtml(l.generatedBy)}</span>
        <span>${escapeHtml(exportedAt)}</span>
      </footer>
    </section>
  `;
}
